/**
 * The Norican corporate mark.
 *
 * Two assets rather than one recoloured file: the mark is navy (#03193d) on
 * light and white on dark, which is how it is used in every other Norican
 * artefact. Tinting a single asset with `currentColor` would be less code but
 * would repaint the logo in whatever the surrounding text colour happens to be,
 * and a brand mark is not text.
 *
 * The swap is driven by the resolved theme from the provider — the same value
 * the toggle reads — so an OS-level change and an explicit toggle can never
 * disagree about which file is showing.
 *
 * `width` is derived from the artwork's own 277.88 x 94.82 viewBox, so the mark
 * can never be stretched by a caller passing an arbitrary box.
 */

import { useTheme } from "@/contexts/theme";

/** Intrinsic aspect ratio of the artwork (277.88 / 94.82). */
const ASPECT_RATIO = 2.9306;

interface NoricanLogoProps {
  /** Rendered height in pixels; width follows the artwork's aspect ratio. */
  height?: number;
  className?: string;
}

export function NoricanLogo({ height = 26, className }: NoricanLogoProps) {
  const { theme } = useTheme();
  const src = theme === "dark" ? "/brand/norican-logo-white.svg" : "/brand/norican-logo-dark.svg";

  return (
    <img
      src={src}
      // The mark is decorative here: it always sits beside the product name, so
      // a screen reader that announced both would read the company twice.
      alt=""
      aria-hidden="true"
      height={height}
      width={Math.round(height * ASPECT_RATIO)}
      className={className}
      // Prevents a visible reflow on first paint before the SVG loads.
      style={{ height, width: Math.round(height * ASPECT_RATIO) }}
    />
  );
}
